import { createAnalytics } from './analytics/createAnalytics';
import type { IAnalytics, IAnalyticsProvider } from './analytics/types';
import { createErrors } from './errors/createErrors';
import type { IErrorProvider, IErrors } from './errors/types';
import { createLogger } from './logging/createLogger';
import type { ILogger, ILoggerProvider } from './logging/types';
import { createMetrics } from './metrics/createMetrics';
import type { IMetrics, IMetricsProvider } from './metrics/types';
import { createTracing } from './tracing/createTracing';
import type { ITracing, ITracingProvider } from './tracing/types';
import type { IObservabilityContext } from './types';

export interface IObservabilityProviders {
  metrics: IMetricsProvider;
  logger: ILoggerProvider;
  errors: IErrorProvider;
  tracing: ITracingProvider;
  analytics: IAnalyticsProvider;
}

export interface IObservability {
  metrics: IMetrics;
  logger: ILogger;
  errors: IErrors;
  tracing: ITracing;
  analytics: IAnalytics;
  providers: IObservabilityProviders;
  setContext(partial: Partial<IObservabilityContext>): void;
  getContext(): IObservabilityContext;
  flush(): Promise<void>;
  shutdown(): Promise<void>;
}

function mergeCtx<T extends Record<string, unknown>>(
  ctx: IObservabilityContext,
  extra?: T,
): (T & { ctx: IObservabilityContext }) | T | { ctx: IObservabilityContext } | undefined {
  if (Object.keys(ctx).length === 0) return extra;
  return { ...extra, ctx: { ...ctx } };
}

export function createObservability(providers: IObservabilityProviders): IObservability {
  let context: IObservabilityContext = {};

  const getContext = (): IObservabilityContext => ({ ...context });

  const setContext = (partial: Partial<IObservabilityContext>): void => {
    context = { ...context, ...partial };
  };

  const loggerProvider: ILoggerProvider = {
    log(level, message, fields) {
      providers.logger.log(level, message, mergeCtx(context, fields));
    },
  };

  const errorProvider: IErrorProvider = {
    capture(error, captureContext) {
      providers.errors.capture(error, mergeCtx(context, captureContext));
    },
  };

  const analyticsProvider: IAnalyticsProvider = {
    track(event, properties) {
      providers.analytics.track(event, mergeCtx(context, properties));
    },
  };

  const all = [
    providers.metrics,
    providers.logger,
    providers.errors,
    providers.tracing,
    providers.analytics,
  ];

  return {
    metrics: createMetrics(providers.metrics),
    logger: createLogger(loggerProvider),
    errors: createErrors(errorProvider),
    tracing: createTracing(providers.tracing),
    analytics: createAnalytics(analyticsProvider),
    providers,
    setContext,
    getContext,
    async flush() {
      await Promise.allSettled(all.map((p) => Promise.resolve(p.flush?.())));
    },
    async shutdown() {
      await Promise.allSettled(all.map((p) => Promise.resolve(p.shutdown?.())));
    },
  };
}
